import type { Stroke } from "./stroke-shape.js";
import { cheapestAssignment } from "./cheapest-assignment.js";
import { strokeCost } from "./stroke-distance.js";

/**
 * What a character costs when its strokes are matched in any order.
 *
 * `handwriting-lookup.ts` pairs the strokes by position, which is what makes a
 * lookup rely on stroke order. This pairs each drawn stroke with whichever
 * stored stroke it fits best, so the answer says how near the ink is to the
 * character with the order left out of it.
 *
 * **The two together are what tells a reader they wrote the right character in
 * the wrong order.** Measured over a 300 character sample, ink that keeps the
 * taught order costs the same either way, to three decimal places and at every
 * percentile. Ink with two adjacent strokes swapped costs 0.129 more in order
 * than out of it at the median. `src/fuxi/grade-handwriting.ts` is what reads
 * the difference and `docs/fuxi.md` has the table.
 *
 * **Only a square matching is asked for.** A handwriting card is marked wrong
 * before it reaches here unless the reader drew exactly as many strokes as the
 * character has, so the two sides are the same size by the time this is
 * called.
 */

/**
 * The cheapest way to pair every drawn stroke with a stored one, per stroke.
 *
 * The assignment problem, solved exactly by `cheapest-assignment.ts`, which
 * has why an approximation will not do here.
 *
 * @param written - The strokes the reader drew, in the order they drew them.
 * @param stored - The character's own strokes, in writing order.
 * @returns The mean cost of the best pairing, or nought where nothing was
 * drawn.
 */
export function orderFreeCost(
  written: readonly Stroke[],
  stored: readonly Stroke[],
): number {
  if (written.length === 0 || written.length !== stored.length) {
    return 0;
  }

  const costs = written.map((stroke) =>
    stored.map((against) => strokeCost(stroke, against)),
  );

  return cheapestAssignment(costs) / written.length;
}

/**
 * The strokes paired in the order they were written.
 *
 * The same sum `characterCost` makes in `handwriting-lookup.ts`, without the
 * penalty for strokes the reader has not drawn. That penalty is what lets a
 * half-written character still bring back a list in the search box, and a card
 * that has already checked the stroke count has nothing for it to say.
 *
 * @param written - The strokes the reader drew, in the order they drew them.
 * @param stored - The character's own strokes, in writing order.
 * @returns The mean cost per stroke, or nought where nothing was drawn.
 */
export function inOrderCost(
  written: readonly Stroke[],
  stored: readonly Stroke[],
): number {
  if (written.length === 0 || written.length !== stored.length) {
    return 0;
  }

  let cost = 0;

  for (let at = 0; at < written.length; at += 1) {
    cost += strokeCost(written[at] as Stroke, stored[at] as Stroke);
  }

  return cost / written.length;
}
